import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/layout";
import { Button, Checkbox, Skeleton, Textarea } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BudgetPdfExportOurWorkPositionsEnum,
  BudgetPdfExportOurWorkViewEnum,
} from "@src/__generated__/graphql";
import {
  Card,
  InputDatePicker,
  type IOption,
  Link,
  NumberInput,
  Select,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency, formatDate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { BankAccountSelect } from "@src/widgets/BankAccountSelect";
import { CompanySelect } from "@src/widgets/CompanySelect";
import { upperFirst } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useLayoutEffect } from "react";
import { EditQuotePageStore } from "./store";

type EditQuotePageProps = {
  id: string;
};

export const EditQuotePage = observer(function EditQuotePage({
  id,
}: EditQuotePageProps) {
  const SHOW_POSITIONS_OPTIONS: IOption[] = [
    {
      label: t`Positions in columns`,
      value: BudgetPdfExportOurWorkPositionsEnum.Columns,
    },
    {
      label: t`Positions in rows`,
      value: BudgetPdfExportOurWorkPositionsEnum.Rows,
    },
    {
      label: t`Do not show positions`,
      value: BudgetPdfExportOurWorkPositionsEnum.Hide,
    },
  ];
  const VIEW_OPTIONS: IOption[] = [
    {
      label: t`Detailed view`,
      value: BudgetPdfExportOurWorkViewEnum.Detailed,
    },
    {
      label: t`Summary view`,
      value: BudgetPdfExportOurWorkViewEnum.Summary,
    },
  ];

  const appStore = useStore();
  const { store } = useLocalObservable(() => ({
    store: new EditQuotePageStore(appStore),
  }));

  useLayoutEffect(() => {
    store.fetchQuote(id);
  }, [id]);

  const form = store.optionsForm?.$;

  if (!form) return null;

  return (
    <Skeleton isLoaded={!store.quoteFetcher.isLoading.value} rounded="xl">
      <Card>
        <Card.Section>
          <Stack
            spacing="4"
            alignItems="start"
            w="full"
            flexDir={{
              base: "column",
              md: "row",
            }}
          >
            <Skeleton
              flex="1"
              w="full"
              maxW={{
                base: "full",
                md: "50%",
              }}
              isLoaded={
                !store.previewQuoteMutator.isLoading.value || !store.previewUrl
              }
              rounded="xl"
            >
              {!!store.previewUrl && (
                <Box
                  as="object"
                  data={store.previewUrl}
                  width="100%"
                  h="850px"
                />
              )}
            </Skeleton>

            <Flex
              flex="1"
              flexDirection="column"
              w="full"
              maxW={{
                base: "full",
                md: "50%",
              }}
            >
              {store.summaryData && (
                <>
                  <Grid
                    templateColumns={{
                      base: "repeat(2, 1fr)",
                      md: "repeat(4, 1fr)",
                    }}
                    gap="2"
                    mt="6"
                    mr="6"
                  >
                    <GridItem>
                      <strong>
                        <Trans>Brand</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>
                      {store.summaryData.brand && (
                        <Link
                          href={{
                            pathname: "/brands/detail",
                            query: {
                              id: store.summaryData.brand.id,
                            },
                          }}
                          target="_blank"
                        >
                          {store.summaryData.brand.name}
                        </Link>
                      )}
                    </GridItem>
                    <GridItem>
                      <strong>
                        <Trans>Price</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight="medium">
                        <strong>
                          {store.summaryData.currency &&
                            currency.formatByCurrency(
                              store.summaryData.price ?? 0,
                              store.summaryData.currency,
                            )}
                        </strong>
                      </Text>
                    </GridItem>

                    <GridItem>
                      <strong>{t`Project`}</strong>
                    </GridItem>
                    <GridItem>
                      {store.summaryData.project && (
                        <Link
                          href={{
                            pathname: "/projects/detail",
                            query: {
                              id: store.summaryData.project.id,
                            },
                          }}
                          target="_blank"
                        >
                          {store.summaryData.project.title}
                        </Link>
                      )}
                    </GridItem>
                    <GridItem>
                      <strong>
                        <Trans>Date</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>{formatDate(store.summaryData.date)}</GridItem>

                    <GridItem>
                      <strong>
                        <Trans>Budgets</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>
                      {store.summaryData.budgetsIds.map((id, index) => (
                        <>
                          <Link
                            key={id}
                            href={{
                              pathname: "/budgets/proposals",
                              query: {
                                budget_id: id,
                              },
                            }}
                            target="_blank"
                          >
                            {id}
                          </Link>
                          {index <
                            (store.summaryData?.budgetsIds.length ?? 0) - 1 && (
                            <Text as="span">,&nbsp;</Text>
                          )}
                        </>
                      ))}
                    </GridItem>
                    <GridItem>
                      <strong>
                        <Trans>Sent to client</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>
                      {formatDate(store.summaryData.sentToClientDate)}
                    </GridItem>

                    <GridItem>
                      <strong>
                        <Trans>Account manager</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>{store.summaryData.accountManager}</GridItem>
                    <GridItem>
                      <strong>
                        <Trans>Client approval</Trans>
                      </strong>
                    </GridItem>
                    <GridItem>
                      {upperFirst(store.summaryData.status.toLowerCase())}
                      {store.summaryData.clientApprovalDate &&
                        `, ${formatDate(store.summaryData.clientApprovalDate)}`}
                    </GridItem>
                  </Grid>

                  <Divider my="4" />
                </>
              )}

              <Stack spacing="6" h="fit-content">
                <Stack>
                  <Text fontSize="lg" fontWeight="medium">
                    <Trans>Quote options</Trans>
                  </Text>
                  <HStack w="full" spacing="2">
                    <InputDatePicker
                      label={t`Date`}
                      w="full"
                      selected={form.date.value ?? undefined}
                      onChange={(range) => {
                        if (!range?.start) return;
                        form.date.onChange(range.start);
                      }}
                    />
                    <NumberInput
                      label={t`Valid for`}
                      value={form.valid_for.value}
                      onChange={(val) => {
                        form.valid_for.onChange(val);
                      }}
                    />
                    <Box>
                      <Select
                        label={t`Language`}
                        placeholder={t`Language`}
                        options={store.languageOptions}
                        value={form.language.value}
                        onChange={form.language.onChange}
                      />
                    </Box>
                  </HStack>
                  <HStack w="full" spacing="2">
                    <Checkbox
                      w="full"
                      onChange={({ target }) => {
                        form.title_page_with_vat.onChange(target.checked);
                      }}
                    >
                      <Trans>Show VAT in title page</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.hide_title_page.value ?? false}
                      onChange={({ target }) => {
                        form.hide_title_page.onChange(target.checked);
                      }}
                    >
                      <Trans>Hide title page</Trans>
                    </Checkbox>
                  </HStack>
                  <HStack w="full" spacing="2">
                    <Checkbox
                      w="full"
                      isChecked={form.add_billing_plan.value ?? false}
                      onChange={({ target }) => {
                        form.add_billing_plan.onChange(target.checked);
                      }}
                    >
                      <Trans>Add billing plan</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.show_disclaimer.value ?? false}
                      onChange={({ target }) => {
                        form.show_disclaimer.onChange(target.checked);
                      }}
                    >
                      <Trans>Show disclaimer</Trans>
                    </Checkbox>
                    {/*
            <Checkbox
              w="full"
              isChecked={form.signature.value ?? false}
              onChange={({ target }) => {
                form.signature.onChange(target.checked)
              }}
            >
              <Trans>Show signature</Trans>
            </Checkbox>
            */}
                  </HStack>
                  <HStack w="full" spacing="2">
                    <Checkbox
                      w="full"
                      isChecked={form.show_client_and_brand.value ?? false}
                      onChange={({ target }) => {
                        form.show_client_and_brand.onChange(target.checked);
                      }}
                    >
                      <Trans>Show client and brand</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.show_project_name_full.value ?? false}
                      onChange={({ target }) => {
                        form.show_project_name_full.onChange(target.checked);
                      }}
                    >
                      <Trans>Show project name and code</Trans>
                    </Checkbox>
                  </HStack>
                  <HStack w="full" spacing="2">
                    <Checkbox
                      w="full"
                      isChecked={
                        form.use_client_name_from_billing_entity.value ?? false
                      }
                      onChange={({ target }) => {
                        form.use_client_name_from_billing_entity.onChange(
                          target.checked,
                        );
                      }}
                    >
                      <Trans>Use client name from billing address</Trans>
                    </Checkbox>
                  </HStack>
                  {form.show_disclaimer.$ && (
                    <Textarea
                      onChange={({ target }) => {
                        store.disclaimerField?.onChange(target.value);
                      }}
                      placeholder={t`Disclaimer`}
                      value={store.disclaimerField?.value ?? ""}
                    />
                  )}

                  {store.showExchangeRateInput && (
                    <NumberInput
                      currency
                      prefix={`1 ${appStore.workspaceStore.settings?.currency.code} =`}
                      prefixStyles={{ w: 16 }}
                      inputStyle={{ paddingLeft: 16 }}
                      suffix={store.quote?.currency.code}
                      label={t`Exchange rate`}
                      value={form.billing_plan_exchange_rate.value}
                      onChange={form.billing_plan_exchange_rate.onChange}
                    />
                  )}

                  <Divider my="2" orientation="horizontal" />

                  <Text fontSize="lg" fontWeight="medium">
                    <Trans>Agency work options</Trans>
                  </Text>
                  <Grid gap="2" templateColumns="repeat(2, 1fr)" w="full">
                    <Select
                      options={SHOW_POSITIONS_OPTIONS}
                      value={form.our_work_positions.value}
                      onChange={(val) => {
                        form.our_work_positions.onChange(val);
                      }}
                      label={t`Show positions`}
                    />
                    <Select
                      label={t`View`}
                      options={VIEW_OPTIONS}
                      value={form.our_work_view.value}
                      onChange={(val) => {
                        form.our_work_view.onChange(val);
                      }}
                    />
                    <Checkbox
                      w="full"
                      isChecked={form.our_work_description.value ?? false}
                      onChange={({ target }) =>
                        form.our_work_description.onChange(target.checked)
                      }
                    >
                      <Trans>Show item descriptions</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.our_work_vat_in_items.value ?? false}
                      onChange={({ target }) =>
                        form.our_work_vat_in_items.onChange(target.checked)
                      }
                    >
                      <Trans>Show VAT in items</Trans>
                    </Checkbox>
                    <Checkbox
                      isChecked={form.show_hours.value}
                      onChange={({ target }) =>
                        form.show_hours.onChange(target.checked)
                      }
                    >
                      <Trans>Show hours</Trans>
                    </Checkbox>
                    <Checkbox
                      isChecked={form.our_work_show_discount.value ?? false}
                      onChange={({ target }) =>
                        form.our_work_show_discount.onChange(target.checked)
                      }
                    >
                      <Trans>Show discount</Trans>
                    </Checkbox>
                  </Grid>

                  <Divider my="2" orientation="horizontal" />

                  <Text fontSize="lg" fontWeight="medium">
                    <Trans>Expenses options</Trans>
                  </Text>
                  <Grid gap="2" templateColumns="repeat(3, 1fr)" w="full">
                    <Checkbox
                      w="full"
                      isChecked={form.expense_show_commission.value ?? false}
                      onChange={({ target }) =>
                        form.expense_show_commission.onChange(target.checked)
                      }
                    >
                      <Trans>Show commission</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.expense_show_quantity.value ?? false}
                      onChange={({ target }) =>
                        form.expense_show_quantity.onChange(target.checked)
                      }
                    >
                      <Trans>Show quantity</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.expense_show_unit.value ?? false}
                      onChange={({ target }) =>
                        form.expense_show_unit.onChange(target.checked)
                      }
                    >
                      <Trans>Show unit</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.expense_show_discount.value ?? false}
                      onChange={({ target }) =>
                        form.expense_show_discount.onChange(target.checked)
                      }
                    >
                      <Trans>Show discount</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.expense_show_description.value ?? false}
                      onChange={({ target }) =>
                        form.expense_show_description.onChange(target.checked)
                      }
                    >
                      <Trans>Show item descriptions</Trans>
                    </Checkbox>
                    <Checkbox
                      w="full"
                      isChecked={form.expense_show_vat_in_items.value ?? false}
                      onChange={({ target }) =>
                        form.expense_show_vat_in_items.onChange(target.checked)
                      }
                    >
                      <Trans>Show VAT in items</Trans>
                    </Checkbox>
                  </Grid>

                  {store.showBillingSelects && (
                    <>
                      <Divider my="2" orientation="horizontal" />

                      <HStack spacing="2" alignItems="start">
                        <Box
                          w="full"
                          maxW={`${100 / store.billingSelectsCount}%`}
                          display={
                            store.hasMultipleBillingEntities ||
                            form.billing_entity_id.hasError
                              ? "block"
                              : "none"
                          }
                        >
                          <Select
                            label={t`Billing entity`}
                            options={store.billingEntityOptions}
                            value={form.billing_entity_id.value}
                            onChange={store.handleSelectBillingEntity}
                            error={form.billing_entity_id.error}
                          />
                        </Box>
                        <Box
                          w="full"
                          maxW={`${100 / store.billingSelectsCount}%`}
                          display={
                            store.hasMultipleCompanies ||
                            form.workspace_company_id.hasError
                              ? "block"
                              : "none"
                          }
                        >
                          <CompanySelect
                            showLabel
                            field={form.workspace_company_id}
                            onChange={store.handleOnSelectLegalEntity}
                          />
                        </Box>
                        <Box
                          w="full"
                          maxW={`${100 / store.billingSelectsCount}%`}
                          display={
                            store.hasMultipleBankAccounts ||
                            form.workspace_bank_account_id.hasError
                              ? "block"
                              : "none"
                          }
                        >
                          <BankAccountSelect
                            showLabel
                            field={form.workspace_bank_account_id}
                            companyId={form.workspace_company_id.value}
                          />
                        </Box>
                      </HStack>
                    </>
                  )}

                  <Divider my="2" orientation="horizontal" />

                  <Checkbox
                    w="full"
                    isChecked={form.save_for_workspace.value ?? false}
                    onChange={({ target }) =>
                      form.save_for_workspace.onChange(target.checked)
                    }
                  >
                    <Trans>Save settings for workspace</Trans>
                  </Checkbox>
                </Stack>

                <Stack
                  justify="space-between"
                  spacing="4"
                  flexDir={{
                    base: "column",
                    md: "row",
                  }}
                >
                  <Button
                    colorScheme="grey"
                    isLoading={store.previewQuoteMutator.isLoading.value}
                    leftIcon={<Icon name="refresh-cw-02" />}
                    onClick={store.refreshPreview}
                    variant="outline"
                  >
                    <Trans>Preview</Trans>
                  </Button>
                  <HStack>
                    <Button
                      w={{
                        base: "full",
                        md: "unset",
                      }}
                      colorScheme="grey"
                      isLoading={store.saveQuoteMutator.isLoading.value}
                      leftIcon={<Icon name="save-01" />}
                      onClick={store.saveQuote}
                      variant="outline"
                    >
                      <Trans>Save</Trans>
                    </Button>
                    <Button
                      w={{
                        base: "full",
                        md: "unset",
                      }}
                      isLoading={store.isDownloadingQuote.value}
                      leftIcon={<Icon name="download-02" />}
                      onClick={store.downloadQuote}
                    >
                      <Trans>Download</Trans>
                    </Button>
                    <Button
                      w={{
                        base: "full",
                        md: "unset",
                      }}
                      isLoading={store.isLoadingSendQuote.value}
                      leftIcon={<Icon name="link-external-01" />}
                      onClick={store.sendQuote}
                    >
                      <Trans>Send</Trans>
                    </Button>
                  </HStack>
                </Stack>
              </Stack>
            </Flex>
          </Stack>
        </Card.Section>
      </Card>
    </Skeleton>
  );
});
